import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useOptimizedKeywordTracking } from '../../pages/NewKeywordTrackingUI/OptimizedKeywordTrackingUIContainer';
import FilterPanel from './FilterPanel';
import { useFilters } from '../../hooks/useFilters';
import { useAnalytics, ANALYTICS_EVENTS } from '../../analytics';

const KeywordTrackingHeader = () => {
  const {
    searchValue,
    setSearchValue,
    handleSearch,
    isSearchMode,
    setIsSearchMode,
    isLoading,
    setHasSubmittedSearch,
    submittedKeyword,
    selectedChip,
    setSelectedChip,
    setCurrentPage,
    showKeywordOnboarding,
    keywordOnboardingStep,
    setKeywordOnboardingStep,
    completeKeywordOnboarding,
    skipAllTutorials,
  } = useOptimizedKeywordTracking();
  const { filterSections } = useFilters();
  const location = useLocation();
  const { trackEvent } = useAnalytics();

  // Mark tutorial as completed when user leaves the page while tutorial is active
  // This component only exists on keyword explorer page, so cleanup runs when navigating away
  useEffect(() => {
    // Cleanup function runs when component unmounts (user navigates away)
    return () => {
      try {
        const tutorialSeen = sessionStorage.getItem('keywordExplorerOnboardingSeen') === 'true';
        const currentStep = sessionStorage.getItem('keywordTutorialCurrentStep');

        // If tutorial is active (not seen and has a current step), mark it as completed
        if (!tutorialSeen && currentStep && (currentStep === '0' || currentStep === '1' || currentStep === '2' || currentStep === '3' || currentStep === '4')) {
          sessionStorage.setItem('keywordExplorerOnboardingSeen', 'true');
          sessionStorage.removeItem('keywordTutorialCurrentStep');
          window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: -1 } }));

          // Try to mark as completed via API (non-blocking)
          fetch('/api/onboarding/complete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ page: 'keyword_explorer' })
          }).catch(() => null);
        }
      } catch (e) {
        // ignore storage errors
      }
    };
  }, []);

  // No automatic search - let user search manually

  const handleSubmit = (e) => {
    e.preventDefault();
    const keyword = searchValue && searchValue.trim() ? searchValue.trim() : (submittedKeyword && submittedKeyword.trim()) || selectedChip || '';
    if (keyword) {
      handleSearch(keyword, 1, convertFiltersToApi(appliedFilters));
    } else {
      handleSearch(searchValue);
    }
  };

  const clearPersistedSearch = () => {
    try {
      sessionStorage.removeItem('keywordTrackingLastResponse');
    } catch (e) {
      // ignore
    }
  };

  const handleClearSearch = () => {
    const keyword = (searchValue && searchValue.trim()) || (submittedKeyword && submittedKeyword.trim()) || selectedChip || '';
    trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_EXPLORER_SEARCH_CLEARED, { keyword });
    setSearchValue('');
    setIsSearchMode(false);
    setHasSubmittedSearch(false);
    setSelectedChip(null);
    setCurrentPage(1);
    // leave filters intact so user can reuse them
    clearPersistedSearch();
  };

  const filterButtonRef = useRef(null);
  const searchButtonRef = useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(() => {
    try {
      const raw = sessionStorage.getItem('keywordTrackingAppliedFilters');
      if (raw) {
        return JSON.parse(raw);
      }
    } catch (e) {
      // ignore
    }
    return {};
  });

  // Restore filters from sessionStorage on mount
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('keywordTrackingAppliedFilters');
      if (raw) {
        const saved = JSON.parse(raw);
        setAppliedFilters(saved || {});
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Position highlight boxes and arrows dynamically
  useEffect(() => {
    if (!showKeywordOnboarding) return;

    const isMobile = window.innerWidth <= 768;
    const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;

    const updatePositions = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const isMobileNow = viewportWidth <= 768;

      // Step 0: Search button
      if (keywordOnboardingStep === 0 && searchButtonRef.current) {
        const rect = searchButtonRef.current.getBoundingClientRect();

        const explanationBox = document.querySelector('.kt-explanation-box--search');
        if (explanationBox) {
          // First reset so we can measure actual size
          explanationBox.style.top = '0px';
          explanationBox.style.left = '0px';

          requestAnimationFrame(() => {
            const boxRect = explanationBox.getBoundingClientRect();

            if (isMobileNow) {
              // Mobile: Position box below the search button, centered
              const gap = 16;
              let boxTop = rect.bottom + gap;
              const boxLeft = 16; // Use CSS left: 16px from media query

              // Ensure box stays within viewport
              if (boxTop + boxRect.height > viewportHeight - 16) {
                boxTop = Math.max(16, viewportHeight - boxRect.height - 16);
              }

              explanationBox.style.top = `${boxTop}px`;
            } else {
              // Desktop/Tablet: Position to the left of button
              const gap = isTablet ? 100 : 140;
              let boxTop = rect.top + rect.height / 2 - boxRect.height / 2;
              let boxLeft = rect.left - boxRect.width - gap;

              // Ensure box stays within viewport — the search box sits near
              // the top of the page, so vertically centering a tall box on
              // it can push its top edge off-screen with no way to scroll
              // it back into view (it's position:fixed).
              if (boxTop < 16) {
                boxTop = 16;
              } else if (boxTop + boxRect.height > viewportHeight - 16) {
                boxTop = viewportHeight - boxRect.height - 16;
              }
              if (boxLeft < 16) {
                boxLeft = 16;
              }

              explanationBox.style.top = `${boxTop}px`;
              explanationBox.style.left = `${boxLeft}px`;

              // Arrow: from the box's edge to the button's actual center
              const arrow = document.querySelector('.kt-arrow-to-search');
              if (arrow) {
                // Draw the line from the box's own edge/center to the
                // BUTTON's actual center point — a purely horizontal line
                // only reaches the button when the box happens to be
                // vertically centered on it; once the viewport clamp above
                // moves the box, the line needs to angle to still connect
                // box to button. Rotation handles both cases correctly.
                const boxCenterY = boxTop + boxRect.height / 2;
                const buttonCenterY = rect.top + rect.height / 2;
                const boxRight = boxLeft + boxRect.width;
                const highlightLeft = rect.left - 4; // highlight border extends 4px beyond button
                const endX = highlightLeft - 10; // small gap so arrow tip does NOT touch highlight
                const dx = endX - boxRight;
                const dy = buttonCenterY - boxCenterY;
                const length = Math.max(Math.sqrt(dx * dx + dy * dy), 0);
                const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

                arrow.style.top = `${boxCenterY}px`;
                arrow.style.left = `${boxRight}px`;
                arrow.style.width = `${length}px`;
                arrow.style.transform = `rotate(${angle}deg)`;
                arrow.style.transformOrigin = '0 50%';
              }
            }
          });
        }
      }

      // Step 1: Filter button
      if (keywordOnboardingStep === 1 && filterButtonRef.current) {
        const rect = filterButtonRef.current.getBoundingClientRect();

        const highlightBox = document.querySelector('.kt-highlight-box--filter');
        if (highlightBox) {
          highlightBox.style.top = `${rect.top - 4}px`;
          highlightBox.style.left = `${rect.left - 4}px`;
          highlightBox.style.width = `${rect.width + 8}px`;
          highlightBox.style.height = `${rect.height + 8}px`;
        }

        const explanationBox = document.querySelector('.kt-explanation-box--filter');
        if (explanationBox) {
          // First reset so we can measure actual size
          explanationBox.style.top = '0px';
          explanationBox.style.left = '0px';

          requestAnimationFrame(() => {
            const boxRect = explanationBox.getBoundingClientRect();

            if (isMobileNow) {
              // Mobile: Position box below the filter button, centered
              const gap = 16;
              let boxTop = rect.bottom + gap;
              const boxLeft = 16; // Use CSS left: 16px from media query

              // Ensure box stays within viewport
              if (boxTop + boxRect.height > viewportHeight - 16) {
                boxTop = Math.max(16, viewportHeight - boxRect.height - 16);
              }

              explanationBox.style.top = `${boxTop}px`;
            } else {
              // Desktop/Tablet: Position to the right of button
              const gap = isTablet ? 40 : 56;
              let boxTop = rect.top + rect.height / 2 - boxRect.height / 2;
              let boxLeft = rect.right + gap;

              // Ensure box stays within viewport — same top/bottom clamp as
              // the search step above, so this box can't clip off-screen
              // vertically either.
              if (boxTop < 16) {
                boxTop = 16;
              } else if (boxTop + boxRect.height > viewportHeight - 16) {
                boxTop = viewportHeight - boxRect.height - 16;
              }
              if (boxLeft + boxRect.width > viewportWidth - 16) {
                boxLeft = viewportWidth - boxRect.width - 16;
              }

              explanationBox.style.top = `${boxTop}px`;
              explanationBox.style.left = `${boxLeft}px`;

              // Arrow + line should sit between button and box, arrowhead touches button, line touches box
              const arrow = document.querySelector('.kt-arrow-to-filter');
              if (arrow) {
                // Arrowhead end is pinned to the button's actual center;
                // the other end needs to reach the box's own center, which
                // can sit above/below that once the viewport clamp moves
                // the box — so angle the line instead of assuming both
                // ends share the same height.
                const buttonCenterY = rect.top + rect.height / 2;
                const boxCenterY = boxTop + boxRect.height / 2;
                const highlightRight = rect.right + 4; // outer edge of highlight
                const arrowheadStartX = highlightRight; // arrowhead touches the highlight edge
                const endAtBox = boxLeft;               // line touches tutorial box
                const dx = endAtBox - arrowheadStartX;
                const dy = boxCenterY - buttonCenterY;
                const length = Math.max(Math.sqrt(dx * dx + dy * dy), 0);
                const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

                arrow.style.top = `${buttonCenterY}px`;
                arrow.style.left = `${arrowheadStartX}px`;
                arrow.style.width = `${length}px`;
                arrow.style.transform = `rotate(${angle}deg)`;
                arrow.style.transformOrigin = '0 50%';
              }
            }
          });
        }
      }
    };

    updatePositions();
    window.addEventListener('resize', updatePositions);
    window.addEventListener('scroll', updatePositions, true);

    return () => {
      window.removeEventListener('resize', updatePositions);
      window.removeEventListener('scroll', updatePositions, true);
    };
  }, [showKeywordOnboarding, keywordOnboardingStep]);

  // Mirror selected chip into the input so chip selection immediately populates the search bar
  useEffect(() => {
    if (selectedChip) {
      setSearchValue(selectedChip);
      setIsSearchMode(false);
      setHasSubmittedSearch(false);
    }
  }, [selectedChip, setHasSubmittedSearch, setIsSearchMode, setSearchValue]);

  // Convert filter selection to API format
  const convertFiltersToApi = (sel) => {
    const apiFilters = {};
    if (sel) {
      if (sel.price) apiFilters.price = sel.price;
      if (sel.rating) apiFilters.rating = sel.rating;
      if (sel.revenue) apiFilters.revenue = sel.revenue;
      if (sel.release_date) apiFilters.release_date = sel.release_date;
      if (sel.genre) apiFilters.genre = sel.genre;
      if (sel.bundle) apiFilters.bundle = sel.bundle;
      if (sel.number_of_players) apiFilters.number_of_players = sel.number_of_players;
      if (sel.platform) apiFilters.platform = sel.platform;
      if (sel.sort) apiFilters.sort_by = sel.sort;
    }
    return apiFilters;
  };

  // Handler to remove individual filter
  const handleRemoveFilter = (key) => {
    const newFilters = { ...(appliedFilters || {}) };
    if (Object.prototype.hasOwnProperty.call(newFilters, key)) {
      delete newFilters[key];
      setAppliedFilters(newFilters);
      try {
        sessionStorage.setItem('keywordTrackingAppliedFilters', JSON.stringify(newFilters || {}));
      } catch (e) {
        // ignore
      }

      // Re-search with updated filters
      const keyword = (searchValue && searchValue.trim()) || (submittedKeyword && submittedKeyword.trim()) || selectedChip || '';
      trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_EXPLORER_FILTER_REMOVED, { keyword, filter_key: key });
      if (keyword) {
        handleSearch(keyword, 1, convertFiltersToApi(newFilters));
      }
    }
  };

  return (
    <header className='ca-header'>
      <div className='ca-header__content'>
        <div className='ca-header__titles'>
          <h1 className='ca-header__title'>
            Keyword Explorer
          </h1>
          <p className='ca-header__subtitle'>
            The Keyword Explorer helps users discover and analyze keyword trends across the Meta and Steam stores in real time.
            It provides deep insights into how different keywords perform, the top apps associated with them, and their download and revenue impact.
            Users can track keywords to stay updated on market shifts, competitor activity, and emerging content opportunities.

          </p>
        </div>
        <form
          className='ca-header__search'
          onSubmit={handleSubmit}
        >
          <div className='ca-search-input'>
            <input
              type="text"
              placeholder="Search here..."
              value={searchValue}
              style={searchValue ? { paddingRight: '36px' } : {}}
              onChange={e => {
                const value = e.target.value;
                // If the user has cleared the input by backspacing to empty,
                // clear the active search so results hide (match behavior of the ✕ button).
                if (value.trim() === '') {
                  handleClearSearch();
                } else {
                  setSearchValue(value);
                }
              }}
            />
            {searchValue && (
              <button
                type="button"
                onClick={handleClearSearch}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#b0b0b5',
                  cursor: 'pointer',
                  padding: '4px',
                  fontSize: '18px',
                  lineHeight: '1'
                }}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            {showKeywordOnboarding && keywordOnboardingStep === 0 && (
              <div className="kt-backdrop-overlay"></div>
            )}
            <button
              ref={searchButtonRef}
              className={`ca-search-button ${showKeywordOnboarding && keywordOnboardingStep === 0 ? 'kt-highlighted' : ''}`}
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="kt-btn-spinner" aria-hidden>
                  <svg width="18" height="18" viewBox="0 0 50 50" style={{ display: 'block' }}>
                    <circle cx="25" cy="25" r="20" fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeDasharray="31.4 31.4">
                      <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.8s" repeatCount="indefinite" />
                    </circle>
                  </svg>
                </span>
              ) : (
                'Search'
              )}
            </button>
          </div>
          {showKeywordOnboarding && keywordOnboardingStep === 0 && (
            <div className="kt-explanation-box kt-explanation-box--search">
              <button
                type="button"
                className="kt-skip-all-btn"
                onClick={() => {
                  skipAllTutorials()
                  sessionStorage.removeItem('keywordTutorialCurrentStep')
                  window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: -1 } }))
                }}
              >
                Skip All
              </button>
              <div className="kt-explanation-text">
                Type a keyword and hit <strong>Search</strong> to explore games and metrics.
              </div>
              <div className="kt-explanation-arrow kt-arrow-to-search"></div>
              <div className="kt-explanation-nav">
                <button
                  type="button"
                  className="kt-nav-btn kt-nav-btn--prev"
                  onClick={() => setKeywordOnboardingStep(1)}
                >
                  Skip
                </button>
                <button
                  type="button"
                  className="kt-nav-btn kt-nav-btn--next"
                  onClick={() => {
                    setKeywordOnboardingStep(1)
                    sessionStorage.setItem('keywordTutorialCurrentStep', '1')
                    window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: 1 } }))
                  }}
                >
                  Next &gt;
                </button>
              </div>
            </div>
          )}
          <div style={{ marginLeft: 8, position: 'relative' }}>
            {showKeywordOnboarding && keywordOnboardingStep === 1 && (
              <>
                <div className="kt-backdrop-overlay"></div>
                <div className="kt-highlight-box kt-highlight-box--filter"></div>
              </>
            )}
            <button
              type="button"
              className={`filter-trigger ${showKeywordOnboarding && keywordOnboardingStep === 1 ? 'kt-highlighted' : ''}`}
              onClick={() => setShowFilters(true)}
              aria-expanded={showFilters}
              ref={filterButtonRef}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: 8 }} xmlns="http://www.w3.org/2000/svg">
                <path d="M10 18h4v-2h-4v2zM4 6h16v2H4V6zm3 6h10v-2H7v2z" fill="#fff" />
              </svg>
              Filter
            </button>
          </div>
          {showKeywordOnboarding && keywordOnboardingStep === 1 && (
            <div className="kt-explanation-box kt-explanation-box--filter">
              <button
                type="button"
                className="kt-skip-all-btn"
                onClick={() => {
                  skipAllTutorials()
                  sessionStorage.removeItem('keywordTutorialCurrentStep')
                  window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: -1 } }))
                }}
              >
                Skip All
              </button>
              <div className="kt-explanation-text">
                Use <strong>Filter</strong> to narrow results by price, ratings, revenue, and more.
              </div>
              <div className="kt-explanation-arrow kt-arrow-to-filter"></div>
              <div className="kt-explanation-nav">
                <button
                  type="button"
                  className="kt-nav-btn kt-nav-btn--prev"
                  onClick={() => {
                    setKeywordOnboardingStep(0)
                    sessionStorage.setItem('keywordTutorialCurrentStep', '0')
                    window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: 0 } }))
                  }}
                >
                  &lt; Prev
                </button>
                <button
                  type="button"
                  className="kt-nav-btn kt-nav-btn--next"
                  onClick={() => {
                    setKeywordOnboardingStep(2)
                    sessionStorage.setItem('keywordTutorialCurrentStep', '2')
                    window.dispatchEvent(new CustomEvent('keywordTutorialStep', { detail: { step: 2 } }))
                  }}
                >
                  Next &gt;
                </button>
              </div>
            </div>
          )}
          {showFilters && (
            <FilterPanel
              open={showFilters}
              onClose={() => setShowFilters(false)}
              sections={filterSections.length > 0 ? filterSections : undefined}
              initialValues={appliedFilters}
              anchorRef={filterButtonRef}
              gap={4}
              onApply={(sel) => {
                // Store the raw filter selection for display
                setAppliedFilters(sel || {});
                try {
                  sessionStorage.setItem('keywordTrackingAppliedFilters', JSON.stringify(sel || {}));
                } catch (e) {
                  // ignore
                }

                // Determine active keyword: input -> submitted -> selected chip
                const keyword = (searchValue && searchValue.trim()) || (submittedKeyword && submittedKeyword.trim()) || selectedChip || '';
                // Convert to API format
                const apiFilters = convertFiltersToApi(sel);

                if (keyword) {
                  const nonEmptyFilters = Object.fromEntries(
                    Object.entries(sel || {}).filter(([, v]) => !(v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)))
                  );
                  trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_EXPLORER_FILTER_APPLIED, { keyword, filters: JSON.stringify(nonEmptyFilters) });
                  handleSearch(keyword, 1, apiFilters);
                } else {
                  console.log('No keyword to search with filters:', apiFilters);
                }
                setShowFilters(false);
              }}
            />
          )}

        </form>

        {/* Applied Filter Badges */}
        {Object.keys(appliedFilters || {}).length > 0 && (
          <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {Object.entries(appliedFilters).map(([key, value]) => {
              if (!value || (Array.isArray(value) && value.length === 0)) return null;
              const displayValue = Array.isArray(value) ? value.join(', ') : value;
              return (
                <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: 'linear-gradient(90deg, rgba(255,154,184,0.12), rgba(246,37,90,0.12))', color: '#F6255A', borderRadius: '6px', fontSize: '12px', fontWeight: 500 }}>
                  <span style={{ textTransform: 'capitalize', marginRight: 4 }}>{key.replace(/_/g, ' ')}:</span>
                  <span style={{ opacity: 0.9, color: '#F6255A' }}>{displayValue}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleRemoveFilter(key);
                    }}
                    aria-label={`Remove filter ${key}`}
                    style={{
                      marginLeft: 4,
                      background: 'transparent',
                      border: 'none',
                      color: '#F6255A',
                      cursor: 'pointer',
                      fontSize: 12,
                      lineHeight: 0.9
                    }}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>
      <style>{`
          .ca-header {
            width: 100%;
            /* This page (/keyword-analysis) has no .page-content wrapper, so this
               margin is the ONLY thing offsetting content below the fixed topbar.
               Matches the app-wide effective offset (real 80px header + 15px
               gutter = 95px, same as .page-content's padding-top) — was
               hardcoded to 64px, which undershot the real header height and let
               content sit partly behind the topbar. */
            margin-top: 95px;
            margin-bottom: 8px;
            position: relative;
            z-index: 1;
          }

          .ca-header__content {
            margin: 0 auto;
            /* No padding-top: .ca-header above already carries the full
               95px topbar-clearance margin-top. Stacking a second top offset
               on this direct child double-counted the topbar gap, pushing
               this page ~44px further down than Category/UGC Explorer etc. */
            display: flex;
            align-items: start;
            flex-direction: column;
            gap: 24px;
          }

          .ca-header__title {
            margin: 0;
            color: #ffffff;
            font-family: 'Rubik', sans-serif;
            font-weight: 600;
            font-size: 32px;
            margin-bottom: 8px;
          }

          .ca-header__subtitle {
            color: #E8E8E8;
            font-family: 'Rubik', sans-serif;
            font-weight: 400;
            font-size: 16px;
            margin-bottom: 0px;
          }

          .ca-header__search {
            display: flex;
            align-items: center;
            gap: 12px;
            width: 100%;
          }

          .ca-search-input {
            width: 100%;
            position: relative;
            max-width: 504px;
          }

          .ca-search-input input {
            width: 100%;
            max-height: 44px;
            height: 100%;
            border-radius: 12px;
            background-color: #21222D;
            border: none;
            outline: none;
            padding: 12px 16px;
            font-family: 'Rubik', sans-serif;
            font-weight: 400;
            font-size: 16px;
            color: #D2D2D2;
          }

          .ca-search-input input::placeholder {
            color: rgba(255, 255, 255, 0.5);
          }

          .ca-search-button {
            padding: 12px 16px 12px 16px;
            border: none;
            font-family: 'Rubik', sans-serif;
            border-radius: 13px;
            background: #F6255A;
            color: #fff;
            font-weight: 600;
            font-size: 16px;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: all 0.3s ease;
          }

          .ca-search-button:disabled {
            background: #6B7280;
            cursor: not-allowed;
            opacity: 0.8;
          }

          .ca-search-button:not(:disabled):hover {
            background: #E91E63;
          }

          .kt-btn-spinner{ display: inline-flex; align-items: center; justify-content: center; }
          .filter-trigger{
            background: transparent;
            border: 1px solid rgba(255,255,255,0.12);
            color: #fff;
            border-radius: 12px;
            padding: 8px 12px;
            display:inline-flex;
            align-items:center;
            gap:6px;
            height:44px;
            cursor:pointer;
            font-weight:600;
          }
          .filter-trigger svg{ opacity:0.95 }

          /* Tutorial backdrop - dims the entire page */
          .kt-backdrop-overlay{
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 999;
            /* Block interaction with everything under the backdrop */
            pointer-events: auto;
            /* No blur here so the highlighted control and tutorial box stay crisp */
          }

          /* Highlight box around the button (match header profile tutorial design) */
          .kt-highlight-box{
            position: fixed;
            border: 2px solid rgba(246, 37, 90, 0.8);
            border-radius: 15px;
            pointer-events: none;
            z-index: 1000;
            box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
            animation: kt-pulse 2s ease-in-out infinite;
          }
          @keyframes kt-pulse {
            0%, 100% { border-color: rgba(246, 37, 90, 0.6); }
            50% { border-color: rgba(246, 37, 90, 1); }
          }
          .kt-highlighted{
            position: relative;
            z-index: 1001;
            animation: kt-button-zoom 2s ease-in-out infinite;
          }
          @keyframes kt-button-zoom {
            0%, 100% { 
              transform: scale(1);
            }
            50% { 
              transform: scale(1.05);
            }
          }

          /* Explanation box with arrow (match header profile tutorial design) */
          .kt-explanation-box{
            position: fixed;
            background: #fff;
            border: 1px solid #ddd;
            border-radius: 12px;
            padding: 20px 16px 12px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
            z-index: 1002;
            min-width: 280px;
            max-width: 320px;
            box-sizing: border-box;
          }
          .kt-skip-all-btn{
            position: absolute;
            top: 8px;
            right: 8px;
            background: transparent;
            border: none;
            color: #999;
            font-size: 12px;
            cursor: pointer;
            padding: 6px 8px;
            min-height: 32px;
            min-width: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
            transition: all 0.2s;
          }
          .kt-skip-all-btn:hover,
          .kt-skip-all-btn:focus{
            color: #F6255A;
            background: rgba(246, 37, 90, 0.1);
            outline: none;
          }
          .kt-skip-all-btn:active{
            transform: scale(0.95);
          }
          .kt-explanation-box--search{
            /* positioned dynamically via JS */
          }
          .kt-explanation-box--filter{
            /* positioned dynamically via JS */
          }
          .kt-explanation-text{
            font-size: 14px;
            color: #333;
            line-height: 1.6;
            margin-top: 15px;
            margin-bottom: 8px;
            word-wrap: break-word;
            white-space: normal;
          }
          .kt-explanation-text strong{
            color: #F6255A;
            font-weight: 600;
          }
          .kt-explanation-arrow{
            position: fixed;
            height: 3px;
            background: #F6255A;
            z-index: 1003;
            pointer-events: none;
          }
          .kt-arrow-to-search,
          .kt-arrow-to-filter{
            /* Positioned dynamically via JavaScript */
          }
          .kt-explanation-arrow::after{
            content: '';
            position: absolute;
            top: 50%;
            width: 0;
            height: 0;
            border-top: 6px solid transparent;
            border-bottom: 6px solid transparent;
            transform: translateY(-50%);
          }
          /* Search step: box on the left, arrow at right end pointing right towards the Search button */
          .kt-arrow-to-search::after{
            left: 100%;
            border-left: 10px solid #F6255A;
          }
          /* Filter step: box on the right, arrow at left end pointing left towards the Filter button */
          .kt-arrow-to-filter::after{
            left: 0;
            border-right: 10px solid #F6255A;
          }
          .kt-explanation-nav{
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 8px;
            padding-top: 10px;
            border-top: 1px solid #eee;
            gap: 8px;
          }
          .kt-nav-btn{
            background: transparent;
            border: none;
            color: #666;
            font-size: 14px;
            cursor: pointer;
            padding: 8px 12px;
            min-height: 44px;
            min-width: 60px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px;
            transition: all 0.2s;
            flex: 1;
            font-weight: 500;
          }
          .kt-nav-btn:hover,
          .kt-nav-btn:focus{
            color: #F6255A;
            background: rgba(246, 37, 90, 0.1);
            outline: none;
          }
          .kt-nav-btn:active{
            transform: scale(0.97);
          }
          .kt-nav-btn--next{
            font-weight: 600;
            color: #F6255A;
            background: rgba(246, 37, 90, 0.1);
          }
          .kt-nav-btn--next:hover,
          .kt-nav-btn--next:focus{
            background: rgba(246, 37, 90, 0.2);
          }

          /* Tablet styles (768px - 1024px) */
          @media (max-width: 1024px) and (min-width: 769px) {
            .kt-explanation-box {
              max-width: 300px;
              padding: 18px 14px 10px;
            }
            .kt-explanation-text {
              font-size: 13px;
            }
          }

          /* Mobile styles */
          @media (max-width: 768px) {
            .ca-header__content {
              flex-direction: column;
              align-items: stretch;
            }

            .ca-header__search {
              justify-content: flex-start;
            }

            .ca-search-input input {
              width: 100%;
            }

            .kt-explanation-box {
              min-width: calc(100vw - 32px);
              max-width: calc(100vw - 32px);
              width: calc(100vw - 32px);
              padding: 20px 16px 14px;
              left: 16px !important;
              right: 16px !important;
              margin: 0 auto;
              border-radius: 16px;
            }

            .kt-explanation-text {
              font-size: 15px;
              line-height: 1.6;
              text-align: left;
              margin-top: 12px;
              margin-bottom: 10px;
            }

            .kt-skip-all-btn {
              top: 10px;
              right: 10px;
              font-size: 13px;
              padding: 8px;
              min-height: 36px;
              min-width: 36px;
            }

            .kt-explanation-nav {
              margin-top: 12px;
              padding-top: 12px;
              gap: 10px;
            }

            .kt-nav-btn {
              font-size: 15px;
              padding: 10px 16px;
              min-height: 48px;
              border-radius: 8px;
            }

            /* Hide arrows on mobile for cleaner look */
            .kt-explanation-arrow {
              display: none;
            }

            /* Adjust backdrop for mobile */
            .kt-backdrop-overlay {
              background: rgba(0, 0, 0, 0.6);
            }
          }

          /* Small mobile styles */
          @media (max-width: 480px) {
            .kt-explanation-box {
              min-width: calc(100vw - 24px);
              max-width: calc(100vw - 24px);
              width: calc(100vw - 24px);
              left: 12px !important;
              right: 12px !important;
              padding: 18px 14px 12px;
            }

            .kt-explanation-text {
              font-size: 14px;
            }

            .kt-nav-btn {
              font-size: 14px;
              padding: 10px 14px;
            }
          }
        `}</style>
    </header>
  );
};

export default KeywordTrackingHeader;